import React, { forwardRef } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { Surface } from './Surface';
import { Button, ButtonProps } from './Button';
import Tooltip from './Tooltip';

export type ToolbarWrapperProps = {
  shouldShowContent?: boolean;
  isVertical?: boolean;
  sx?: SxProps<Theme>;
  children?: React.ReactNode;
} & Omit<React.HTMLProps<HTMLDivElement>, 'className'>;

const ToolbarWrapper = forwardRef<HTMLDivElement, ToolbarWrapperProps>(
  (
    { shouldShowContent = true, children, isVertical = false, sx, ...rest },
    ref
  ) => {
    return (
      <>
        {shouldShowContent && (
          <Surface
            sx={{
              display: 'inline-flex',
              height: '100%',
              lineHeight: 'none',
              gap: 0.5,
              ...(isVertical
                ? { flexDirection: 'column', padding: 1 }
                : { flexDirection: 'row', padding: 0.5, alignItems: 'center' }),
              ...sx,
            }}
            {...rest}
            ref={ref}
          >
            {children}
          </Surface>
        )}
      </>
    );
  }
);

ToolbarWrapper.displayName = 'Toolbar';

export type ToolbarDividerProps = {
  horizontal?: boolean;
  sx?: SxProps<Theme>;
} & Omit<React.HTMLProps<HTMLDivElement>, 'className' | 'ref'>;

const ToolbarDivider = forwardRef<HTMLDivElement, ToolbarDividerProps>(
  ({ horizontal, sx, ...rest }, ref) => {
    return (
      <Box
        sx={{
          bgcolor: 'divider',
          ...(horizontal
            ? {
                width: '100%',
                minWidth: '1.5rem',
                height: '1px',
                my: 0.5,
                '&:first-of-type': { mt: 0 },
                '&:last-of-type': { mb: 0 },
              }
            : {
                height: '100%',
                minHeight: '1.5rem',
                width: '1px',
                mx: 0.5,
                '&:first-of-type': { ml: 0 },
                '&:last-of-type': { mr: 0 },
              }),
          ...sx,
        }}
        ref={ref}
        {...rest}
      />
    );
  }
);

ToolbarDivider.displayName = 'Toolbar.Divider';

export type ToolbarButtonProps = {
  active?: boolean;
  activeClassname?: string;
  tooltip?: string;
  tooltipShortcut?: string[];
  buttonSize?: ButtonProps['buttonSize'];
  variant?: ButtonProps['variant'];
  sx?: SxProps<Theme>;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
  children?: React.ReactNode;
} & Omit<React.HTMLAttributes<HTMLButtonElement>, 'className' | 'color'>;

const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  (
    {
      children,
      buttonSize = 'icon',
      variant = 'ghost',
      sx,
      tooltip,
      tooltipShortcut,
      activeClassname,
      ...rest
    },
    ref
  ) => {
    const content = (
      <Button
        activeClassname={activeClassname}
        sx={{
          gap: 0.5,
          minWidth: '2rem',
          px: 1,
          width: 'auto',
          ...sx,
        }}
        variant={variant}
        buttonSize={buttonSize}
        ref={ref}
        {...rest}
      >
        {children}
      </Button>
    );

    if (tooltip) {
      return (
        <Tooltip title={tooltip} shortcut={tooltipShortcut}>
          {content}
        </Tooltip>
      );
    }

    return content;
  }
);

ToolbarButton.displayName = 'ToolbarButton';

export const Toolbar = {
  Wrapper: ToolbarWrapper,
  Divider: ToolbarDivider,
  Button: ToolbarButton,
};
